import type { IContainer } from '@monofe/di';
import { injectOrBuildContainer } from '@monofe/di';
import { useEffect, useState } from 'react';
import type { IBrowser } from '../interface/browser';
import { injectBrowser } from '../interface/browser';
import type { IFetcher } from '../interface/fetch';
import { injectFetch } from '../interface/fetch';
import type { IKVStorage } from '../interface/kvStorage';
import { injectKVStorage } from '../interface/kvStorage';
import type { ITranslationStorage } from '../interface/translationStorage';
import { injectTranslationStorage } from '../interface/translationStorage';
import type { IZip } from '../interface/zip';
import { injectZip } from '../interface/zip';

export function useInjector<T>(resolver: Promise<T | null>): T | null {
    const [item, setItem] = useState<T | null>(null);

    useEffect(() => {
        resolver
            .then(val => {
                setItem(val);
            })
            .catch(err => {
                throw err;
            });
    }, [setItem, resolver]);

    return item;
}

export function useFetch(container: IContainer = injectOrBuildContainer()): IFetcher | null {
    return useInjector<IFetcher>(Promise.resolve(injectFetch(container)));
}

export function useZip(container: IContainer = injectOrBuildContainer()): IZip | null {
    return useInjector<IZip>(Promise.resolve(injectZip(container)));
}

export function useKVStorage(container: IContainer = injectOrBuildContainer()): IKVStorage | null {
    return useInjector<IKVStorage>(Promise.resolve(injectKVStorage(container)));
}

export function useBrowser(container: IContainer = injectOrBuildContainer()): IBrowser | null {
    return useInjector<IBrowser>(Promise.resolve(injectBrowser(container)));
}

export function useTranslationStorage(
    container: IContainer = injectOrBuildContainer(),
): ITranslationStorage | null {
    return useInjector<ITranslationStorage>(Promise.resolve(injectTranslationStorage(container)));
}

export function useKVState<T>(
    key: string,
    kvFetch: Promise<IKVStorage>,
    initial: T,
): [T, (newValue: T) => void] {
    const [state, setState] = useState<T>(initial);

    const setValue = (newValue: T) => {
        kvFetch
            .then(kv => kv.setItem(key, newValue))
            .catch(error => {
                throw error;
            });
        setState(newValue);
    };

    kvFetch
        .then(kv => kv.getItem(key))
        .then(val => {
            setState(val as T);
        })
        .catch(error => {
            throw error;
        });

    return [state, setValue];
}
